import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import {
  Box, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import ListBlankState from '@components/ListBlankState'
import NotificationBannerLocation from '@components/NotificationBannerLocation'
import NotificationModuleLocation from '@components/NotificationModuleLocation'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import type { DefaultContext, PageContextPayload } from '@contexts/pageContext'
import PageContext from '@contexts/pageContext'

import useAgencySelector from '@hooks/useAgencySelector'
import useCampaignContributions from '@hooks/useCampaignContributions'

import generateNavigationItems from
  '@sections/Client/packs/Accounting/components/AccountingNavigation/utils/generateNavigationItems'

import { paidByOptions, paymentMethods } from '@functions/campaignContribution'

import ContributionsListItem from '../ContributionListItem'

const buildPageFilterFields = () => {
  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        label: 'Filter by Address',
        entityKey: 'campaigns',
      },
      key: 'campaign_id',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by ID or Reference',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        inputType: 'date',
        style,
      },
      key: 'start_date',
      label: 'Received After',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        inputType: 'date',
        style,
      },
      key: 'end_date',
      label: 'Received Before',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'select',
        style,
      },
      key: 'payment_method',
      label: 'Payment Method',
      values: paymentMethods.map(option => ({ label: option.text, key: option.key })),
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All',
        inputComponent: 'select',
        style,
      },
      key: 'paid_by',
      label: 'Paid By',
      values: paidByOptions.map(option => ({ label: option.text, key: option.key })),
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All',
        inputComponent: 'select',
        style,
      },
      key: 'payment_received',
      label: 'Payment Received',
      values: [
        { key: 'true', label: 'Yes' },
        { key: 'false', label: 'No' },
      ],
    },
  ]
}

interface PageContentProps {
  pageFilters: {},
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props
  // useLocation to refresh the window.location. If not used location won't reload if URL changes
  useLocation()

  const { selectedAgencyId } = useAgencySelector()

  const campaignContributionsPayload = useCampaignContributions({
    filters: {
      ...pageFilters,
      agency_id: selectedAgencyId,
    },
    requestOptions: {
      include_campaign_contribution_campaign: true,
    },
    performHttpRequests: true,
  })
  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredCampaignContributions,
    filteredCampaignContributionsCount,
    hasFilteredCampaignContributions,
    loading,
  } = campaignContributionsPayload

  const pageContext = useContext<DefaultContext<PageContextPayload>>(PageContext)
  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  // @ts-expect-error TS2345
  const navItems = generateNavigationItems(selectedAgencyId)
  const items = navItems?.sections[0]?.items || []

  return (
    <>
      <Helmet>
        <title>Contributions | Realhub</title>
      </Helmet>

      <PageHeader
        activeTabBarItemKey="campaign-contributions"
        nestedNavigation
        tabBarItems={items}
        title="Contributions"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <NotificationBannerLocation locationKey="agency.agencyManager.campaignContributions.banner" />

            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredCampaignContributions && <ListBlankState />}

            {!loading && hasFilteredCampaignContributions && (
              filteredCampaignContributions.map(campaignContribution => (
                <ContributionsListItem
                  campaignContribution={campaignContribution}
                  key={campaignContribution.id}
                />
              ))
            )}

            {canLoadMore && (
              <DashboardModule.LoadMoreFooter
                boxProps={{ marginBottom: 'large', variant: 'white' }}
                callbacks={{ loadMore }}
                canLoadMore={canLoadMore}
                entityCount={filteredCampaignContributionsCount}
                loading={loading}
              />
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <NotificationModuleLocation locationKey="agency.agencyManager.campaignContributions.sidebar" />

            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields()}
              pageFilters={pageFilters}
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}
export default PageContent
